import DayjsExtended from "@logic/DayjsExtended";
import { ChooseCreatorRecommendedDaysLimit, DaysBetweenOverlappingVisitChooseCreatorDates } from "@constants/collab";
import { CollabInput, OverlappingVisitsScheduleUpdateItem, VisitMarkedReadyToPost } from "@contracts/collab";

export type OverlappingVisitQueueRow = VisitMarkedReadyToPost & {
  isCurrent?: boolean;
};

export function formatChooseCreatorDateForInput(date: string | null | undefined): string | null {
  if (!date) return null;
  return DayjsExtended(date).format("YYYY-MM-DD");
}

export function defaultChooseCreatorDate(): string {
  return DayjsExtended().add(ChooseCreatorRecommendedDaysLimit, "days").format("YYYY-MM-DD");
}

export function cascadeChooseCreatorDates(queue: OverlappingVisitQueueRow[]): OverlappingVisitQueueRow[] {
  const result = queue.map(row => ({ ...row }));
  for (let i = 1; i < result.length; i++) {
    const previousDate = result[i - 1].chooseCreatorEstimatedDate;
    if (!previousDate) continue;
    result[i] = {
      ...result[i],
      chooseCreatorEstimatedDate: DayjsExtended(previousDate).add(DaysBetweenOverlappingVisitChooseCreatorDates, "days").format("YYYY-MM-DD"),
    };
  }
  return result;
}

/** Swap two queue rows; choose-by dates stay on their list positions (assigned to whichever visit moves into that slot). */
export function swapQueueRowsKeepingSlotDates(queue: OverlappingVisitQueueRow[], indexA: number, indexB: number): OverlappingVisitQueueRow[] {
  const next = queue.map(row => ({ ...row }));
  const dateAtA = next[indexA].chooseCreatorEstimatedDate;
  const dateAtB = next[indexB].chooseCreatorEstimatedDate;
  [next[indexA], next[indexB]] = [next[indexB], next[indexA]];
  next[indexA].chooseCreatorEstimatedDate = dateAtA;
  next[indexB].chooseCreatorEstimatedDate = dateAtB;
  return next;
}

export function renormalizePostToCreatorsOrder(queue: OverlappingVisitQueueRow[]): OverlappingVisitQueueRow[] {
  let order = 1;
  return queue.map(row => {
    if (row.postedToCreators) {
      return { ...row };
    }
    const updated = { ...row, postToCreatorsOrder: order };
    order += 1;
    return updated;
  });
}

/** Matches backend GetVisitsMarkedReadyToPost ordering. */
export function sortOverlappingQueueVisits(visits: ReadonlyArray<VisitMarkedReadyToPost>): VisitMarkedReadyToPost[] {
  return [...visits].sort((a, b) => {
    const orderA = a.postToCreatorsOrder ?? -1;
    const orderB = b.postToCreatorsOrder ?? -1;
    if (orderA !== orderB) return orderA - orderB;
    const readyA = a.readyToPost ?? "";
    const readyB = b.readyToPost ?? "";
    return readyA.localeCompare(readyB);
  });
}

export function getVisitAheadInQueue(visits: ReadonlyArray<VisitMarkedReadyToPost>, currentCollabInputId: string): VisitMarkedReadyToPost | null {
  const sorted = sortOverlappingQueueVisits(visits);
  const index = sorted.findIndex(v => v.id === currentCollabInputId);
  if (index <= 0) return null;
  return sorted[index - 1];
}

const defaultReadyToPostStatusHeading = "Sending to Creators soon";

export function resolveReadyToPostStatusHeading(visits: ReadonlyArray<VisitMarkedReadyToPost> | null | undefined, currentCollabInputId: string): string {
  if (!visits?.length) return defaultReadyToPostStatusHeading;
  const ahead = getVisitAheadInQueue(visits, currentCollabInputId);
  if (!ahead) return defaultReadyToPostStatusHeading;
  return `Waiting on Creator Selection for ${ahead.name || "Untitled visit"}`;
}

export function shouldLoadOverlappingQueueForStatus(collabInput: Pick<CollabInput, "readyToPost" | "postedToCreators" | "createdForCreatorId" | "postToCreatorsOrder">, enableOverlappingPostsLogic: boolean): boolean {
  if (!enableOverlappingPostsLogic || collabInput.createdForCreatorId) return false;
  if (!collabInput.readyToPost || collabInput.postedToCreators) return false;
  return collabInput.postToCreatorsOrder != null;
}

export function buildScheduleUpdatePayload(queue: OverlappingVisitQueueRow[]): OverlappingVisitsScheduleUpdateItem[] {
  const normalized = renormalizePostToCreatorsOrder(queue);
  return normalized.map(row => ({
    collabInputId: row.id,
    chooseCreatorEstimatedDate: row.chooseCreatorEstimatedDate,
    chooseCreatorEstimatedNotes: row.chooseCreatorEstimatedNotes,
    postToCreatorsOrder: row.postedToCreators ? row.postToCreatorsOrder : row.postToCreatorsOrder,
  }));
}

export function appendPendingCurrentVisit(
  visits: VisitMarkedReadyToPost[],
  currentCollabInputId: string,
  currentName: string,
  chooseCreatorEstimatedDate: string | null,
  chooseCreatorEstimatedNotes: string | null
): OverlappingVisitQueueRow[] {
  if (visits.some(v => v.id === currentCollabInputId)) {
    return visits.map(v => ({ ...v, isCurrent: v.id === currentCollabInputId }));
  }
  const maxOrder = visits.reduce((max, v) => Math.max(max, v.postToCreatorsOrder ?? 0), 0);
  const pendingRow: OverlappingVisitQueueRow = {
    id: currentCollabInputId,
    name: currentName,
    readyToPost: null,
    postedToCreators: null,
    postToCreatorsOrder: maxOrder + 1,
    chooseCreatorEstimatedDate: formatChooseCreatorDateForInput(chooseCreatorEstimatedDate) ?? defaultChooseCreatorDate(),
    chooseCreatorEstimatedNotes: chooseCreatorEstimatedNotes,
    isCurrent: true,
  };
  return cascadeChooseCreatorDates([...visits.map(v => ({ ...v })), pendingRow]);
}

/** True when at least one other visit (not the current collab) is in the overlapping queue. */
export function hasOverlappingQueuePeers(visits: ReadonlyArray<{ id: string }>, currentCollabInputId: string): boolean {
  return visits.some(v => v.id !== currentCollabInputId);
}

export function applyUpdatedCollabInputsInMemory(collabs: CollabInput[] | null | undefined, updatedCollabInputs: CollabInput[]): void {
  if (!collabs?.length) return;
  for (const updated of updatedCollabInputs) {
    const index = collabs.findIndex(c => c.id === updated.id);
    if (index >= 0) {
      Object.assign(collabs[index], updated);
    }
  }
}

export function isQueueDateValid(queue: OverlappingVisitQueueRow[]): boolean {
  const today = DayjsExtended().startOf("day");
  return queue.every(row => {
    if (!row.chooseCreatorEstimatedDate) return false;
    return DayjsExtended(row.chooseCreatorEstimatedDate).isSameOrAfter(today, "day");
  });
}
